import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { CashierPaymentMethod, CurrencyPrimitive } from "@/api/cashier";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useFormatDownTime } from "@/hooks/useFormatDownTime";
import { moneyToMajorUnits } from "@/utils/helpers";

export const usePaymentMethodProperties = () => {
  const { t } = useTranslation();
  const { formatNumber, formatFee } = useFormatAmount();
  const { formatDowntime } = useFormatDownTime();

  const getPaymentMethodProperties = useCallback(
    ({
      paymentMethod,
      tradingCurrency,
    }: {
      paymentMethod: CashierPaymentMethod;
      tradingCurrency: CurrencyPrimitive;
    }) => {
      const baseSettings =
        "deposit" in paymentMethod.base_settings
          ? paymentMethod.base_settings.deposit
          : paymentMethod.base_settings.payout;

      const settings = paymentMethod.settings?.[tradingCurrency.code];
      const feeValueAsNumber = settings?.fee
        ? typeof settings.fee.value === "number"
          ? settings.fee.value
          : moneyToMajorUnits(settings.fee.value)
        : null;
      const processingMessage = [
        baseSettings.expected_processing_time,
        settings?.fee &&
          (feeValueAsNumber === 0
            ? t("deposit.methods.properties.free")
            : formatFee(settings.fee, tradingCurrency)),
      ]
        .filter(Boolean)
        .join(" / ");

      const downtime = paymentMethod.downtime
        ? `${formatDowntime(paymentMethod.downtime)}`
        : undefined;

      const limitSettings =
        paymentMethod.settings?.[tradingCurrency.code]?.limit;

      let min: number | null;
      let max: number | null;

      if (limitSettings) {
        min = limitSettings.min.unlimited
          ? null
          : moneyToMajorUnits(limitSettings.min.value);
        max = limitSettings.max.unlimited
          ? null
          : moneyToMajorUnits(limitSettings.max.value);
      } else {
        const fallbackLimitSettings = paymentMethod.settings
          ? Object.values(paymentMethod.settings)[0]?.trading_currency?.limit
          : undefined;
        min = moneyToMajorUnits(fallbackLimitSettings?.min) ?? null;
        max = moneyToMajorUnits(fallbackLimitSettings?.max) ?? null;
      }

      const isMinSet = min != null;
      const isMaxSet = max != null;
      const limitsMessage =
        isMinSet && isMaxSet
          ? t("deposit.methods.properties.limit_range", {
              min: formatNumber(min, tradingCurrency),
              max: formatNumber(max, tradingCurrency),
              currency: tradingCurrency.code,
            })
          : !isMinSet && isMaxSet
            ? t("deposit.methods.properties.limit_maximum", {
                value: formatNumber(max, tradingCurrency),
                currency: tradingCurrency.code,
              })
            : isMinSet && !isMaxSet
              ? t("deposit.methods.properties.limit_minimum", {
                  value: formatNumber(min, tradingCurrency),
                  currency: tradingCurrency.code,
                })
              : t("deposit.methods.properties.no_limit");

      return [processingMessage, downtime, limitsMessage].filter(Boolean);
    },
    [t, formatNumber, formatFee, formatDowntime]
  );

  return {
    getPaymentMethodProperties,
  };
};
